import React, { useState } from 'react';
import type { ApprovalActionType, ApprovalPayload, ApprovalReason } from '../../../../../services/approvals/approvals.service';
import type { OrderlineView } from '../../../../../services/orderlines/orderlines.service';
import { Button } from '../../../../../components/Button/Button';
import classes from './approval-request-modal.module.scss';

const ACTION_LABELS: Record<ApprovalActionType, string> = {
  global_discount: 'הנחה כללית',
  item_discount: 'הנחה על פריט',
  cancel: 'ביטול',
  on_the_house: 'על חשבון הבית',
  general_item: 'פריט כללי',
  transfer: 'העברה',
  merge: 'מיזוג שולחנות',
  split_merged_table: 'פיצול שולחן ממוזג',
  send_items: 'שליחת פריטים',
};

const getActionLabel = (actionType: ApprovalActionType, payload: ApprovalPayload): string => {
  if (payload.actionType === 'cancel' && payload.cancelScope === 'order') {
    return 'ביטול כל ההזמנה';
  }
  return ACTION_LABELS[actionType];
};

type Reason = { value: ApprovalReason; label: string; emoji: string };

// RTL 2-col grid: col-right first, col-left second per row
const REASONS: Reason[] = [
  { value: 'other', label: 'אחר', emoji: '✏️' },
  { value: 'cancel_order', label: 'ביטול', emoji: '🚫' },
  { value: 'waiter_error', label: 'מנה שגויה', emoji: '🍽️' },
  { value: 'vip', label: 'VIP', emoji: '👑' },
  { value: 'staff', label: 'שת"פ', emoji: '🤝' },
  { value: 'team', label: 'צוות', emoji: '🧑‍🧒‍🧒' },
];

const formatMoney = (v: number) => `₪${v.toFixed(1).replace(/\.0$/, '')}`;

type AffectedItem = { label: string; qty: number; price?: number };

const getAffectedItems = (payload: ApprovalPayload, orderlines: OrderlineView[]): AffectedItem[] => {
  const nameById = new Map(orderlines.map((ol) => [ol.id, ol.productName ?? 'פריט כללי']));
  switch (payload.actionType) {
    case 'item_discount':
      return payload.items.map((item) => ({
        label: nameById.get(item.orderlineId) ?? `פריט #${item.orderlineId}`,
        qty: item.quantity,
        price: item.discountInput,
      }));
    case 'on_the_house': {
      // OTH stores the full gross; show the net amount being comped (gross − existing).
      const existingById = new Map(orderlines.map((ol) => [ol.id, ol.discountInput ?? 0]));
      return payload.items.map((item) => ({
        label: nameById.get(item.orderlineId) ?? `פריט #${item.orderlineId}`,
        qty: item.quantity,
        price: Math.max(0, item.discountInput - (existingById.get(item.orderlineId) ?? 0)),
      }));
    }
    case 'cancel':
      return payload.items.map((item) => ({
        label: nameById.get(item.orderlineId) ?? `פריט #${item.orderlineId}`,
        qty: item.quantity,
      }));
    case 'general_item':
      return [{ label: 'פריט כללי', qty: payload.quantity, price: payload.unitPrice }];
    case 'global_discount':
      return [{
        label: payload.discountMode === 'percentage'
          ? `הנחה ${payload.discountInput}%`
          : `הנחה ${formatMoney(payload.discountInput)}`,
        qty: 1,
      }];
    case 'transfer':
      return [{ label: `הזמנה #${payload.sourceOrderId}`, qty: payload.orderlineMoves.reduce((sum, item) => sum + item.quantity, 0) }];
    case 'merge':
      return [{ label: 'שולחנות שנבחרו למיזוג', qty: payload.selectedTableIds.length }];
    case 'split_merged_table':
      return [{ label: 'שולחנות שנבחרו לפיצול', qty: payload.linkIds.length }];
    case 'send_items':
      return payload.items.map((item) => ({
        label: nameById.get(item.orderlineId) ?? `פריט #${item.orderlineId}`,
        qty: item.quantity,
      }));
  }
};

export type ApprovalRequestModalProps = {
  opened: boolean;
  onClose: () => void;
  actionType: ApprovalActionType;
  payload: ApprovalPayload;
  orderlines: OrderlineView[];
  onSubmit: (reason: ApprovalReason, note: string | null) => Promise<void>;
  isSubmitting: boolean;
  /** managers auto-approve — no request is actually sent, just logged, so the copy reflects that */
  isManager?: boolean;
};

export const ApprovalRequestModal: React.FC<ApprovalRequestModalProps> = ({
  opened,
  onClose,
  actionType,
  payload,
  orderlines,
  onSubmit,
  isSubmitting,
  isManager = false,
}) => {
  const [selectedReason, setSelectedReason] = useState<ApprovalReason | null>(null);
  const [note, setNote] = useState('');

  if (!opened) return null;

  const canSubmit = selectedReason !== null && (selectedReason !== 'other' || note.trim().length > 0) && !isSubmitting;
  const affectedItems = getAffectedItems(payload, orderlines);

  const handleSubmit = async () => {
    if (!selectedReason) return;
    await onSubmit(selectedReason, selectedReason === 'other' ? note.trim() : null);
    setSelectedReason(null);
    setNote('');
  };

  const handleClose = () => {
    setSelectedReason(null);
    setNote('');
    onClose();
  };

  return (
    <div className={classes.overlay} onClick={() => { if (!isSubmitting) handleClose(); }}>
      <div className={classes.modal} data-testid="approval-request-modal" onClick={(e) => e.stopPropagation()}>
        <div className={classes.header}>
          <h2 className={classes.title}>{isManager ? 'בחר סיבה' : 'שלח בקשה למנהל'}</h2>
          <p className={classes.subtitle}>{getActionLabel(actionType, payload)}</p>
        </div>

        <div className={classes.body}>
          <div className={classes.warning}>
            <span className={classes.warningIcon}>{'⚠️'}</span>
            <span>{isManager
              ? 'פעולה זו תירשם ביומן המשמרת. יש לוודא שהסיבה נכונה לפני האישור.'
              : 'פעולה זו תרשם ביומן המשמרת ותדרוש אישור מנהל. יש לוודא שהסיבה נכונה לפני האישור.'}</span>
          </div>
          {selectedReason === 'other' && (
            <textarea
              className={classes.noteInput}
              data-testid="approval-request-note"
              placeholder={'פירוט סיבה'}
              value={note}
              onChange={(e) => setNote(e.currentTarget.value)}
              rows={1}
              autoFocus
            />
          )}

          <div className={`${classes.sectionLabel} ${classes.reasonSectionLabel}`}>{'בחר סיבה'}</div>
          <div className={classes.reasonGrid}>
            {REASONS.map((r) => (
              <button
                key={r.value}
                type="button"
                disabled={isSubmitting}
                className={`${classes.reasonCard} ${selectedReason === r.value ? classes.reasonCardSelected : ''}`}
                data-testid={`approval-request-reason-${r.value}`}
                onClick={() => {
                  setSelectedReason(r.value);
                  if (r.value !== 'other') setNote('');
                }}
              >
                <span className={classes.reasonEmoji}>{r.emoji}</span>
                <span className={classes.reasonLabel}>{r.label}</span>
                {selectedReason === r.value && (
                  <span className={classes.checkmark}>{'✓'}</span>
                )}
              </button>
            ))}
          </div>


        </div>

        {affectedItems.length > 0 && (
          <div className={classes.affectedSection}>
            <div className={classes.sectionLabel}>{'פריטים מושפעים'}</div>
            <div className={classes.affectedList}>
              {affectedItems.map((item, i) => (
                <div key={i} className={classes.affectedRow}>
                  <span className={classes.affectedQty}>{item.qty}</span>
                  <span className={classes.affectedLabel}>{item.label}</span>
                  {item.price !== undefined && (
                    <span className={classes.affectedPrice}>{formatMoney(item.price)}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className={classes.footer}>
          <Button variant="cancel" onClick={handleClose} disabled={isSubmitting} dataTestId="approval-request-cancel" fullWidth>
            {'ביטול'}
          </Button>
          <Button variant="confirm" onClick={handleSubmit} disabled={!canSubmit} loading={isSubmitting} dataTestId="approval-request-submit" fullWidth>
            {'שלח בקשה'}
          </Button>
        </div>
      </div>
    </div>
  );
};
